// Customer profile create/edit form (mig 0017), shown in a dialog from the
// profiles pages, the customer page and the account form. A profile sits on
// one customer platform; whether it names a tester (and needs a label)
// follows that platform's profile mode.

import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Field, selectClass, testerLabel } from '@/components/ui/field';
import { customerPlatforms, customerProfiles, customers, lookups, testers } from '@/lib/api';
import { PROFILE_MODES, PROFILE_TEXT_MAX, isValidEmail, platformLabel } from '@frt/shared-types';
import type { CustomerProfileDetail, ProfileAddress } from '@frt/shared-types';
import { PhonePicker } from '../assets/asset-forms';
import {
  OobEditor,
  newOobDraft,
  oobDraftFromEntry,
  oobDraftToInput,
  validateOobDraft,
  type OobDraft,
  type OobErrors,
} from './oob-editor';

/** Pre-selections when creating from a customer page or the account form. */
export interface ProfilePreset {
  frt_customer_id?: number;
  customer_platform_id?: number;
  tester_id?: number | null;
}

const ADDRESS_FIELDS: { key: keyof ProfileAddress; label: string; wide?: boolean }[] = [
  { key: 'address_line1', label: 'Address line 1', wide: true },
  { key: 'address_line2', label: 'Address line 2', wide: true },
  { key: 'city', label: 'City' },
  { key: 'state', label: 'State' },
  { key: 'postal_code', label: 'Postal code' },
  { key: 'country', label: 'Country' },
];

export function modeLabel(mode: string): string {
  return PROFILE_MODES.find((m) => m.value === mode)?.label ?? mode;
}

export function ProfileFormDialog({
  open,
  onClose,
  profile,
  preset,
  onSaved,
}: {
  open: boolean;
  onClose: () => void;
  /** Present = edit, absent = create. */
  profile?: CustomerProfileDetail;
  preset?: ProfilePreset;
  onSaved: (p: CustomerProfileDetail) => void;
}) {
  const queryClient = useQueryClient();
  const editing = Boolean(profile);

  const blankAddress = Object.fromEntries(ADDRESS_FIELDS.map((f) => [f.key, ''])) as Record<
    keyof ProfileAddress,
    string
  >;
  const [customerId, setCustomerId] = useState(0);
  const [platformId, setPlatformId] = useState(0);
  const [testerId, setTesterId] = useState(0);
  const [label, setLabel] = useState('');
  const [address, setAddress] = useState(blankAddress);
  const [phoneId, setPhoneId] = useState<number | null>(null);
  const [email, setEmail] = useState('');
  const [notes, setNotes] = useState('');
  const [oob, setOob] = useState<OobDraft[]>(() => [newOobDraft()]);
  const [showErrors, setShowErrors] = useState(false);

  useEffect(() => {
    if (!open) return;
    setShowErrors(false);
    if (profile) {
      setCustomerId(profile.frt_customer_id);
      setPlatformId(profile.customer_platform_id);
      setTesterId(profile.tester_id ?? 0);
      setLabel(profile.label ?? '');
      setAddress(
        Object.fromEntries(ADDRESS_FIELDS.map((f) => [f.key, profile[f.key] ?? ''])) as Record<
          keyof ProfileAddress,
          string
        >,
      );
      setPhoneId(profile.phone_id);
      setEmail(profile.email ?? '');
      setNotes(profile.notes ?? '');
      setOob(profile.oob_entries.length > 0 ? profile.oob_entries.map(oobDraftFromEntry) : [newOobDraft()]);
    } else {
      setCustomerId(preset?.frt_customer_id ?? 0);
      setPlatformId(preset?.customer_platform_id ?? 0);
      setTesterId(preset?.tester_id ?? 0);
      setLabel('');
      setAddress(blankAddress);
      setPhoneId(null);
      setEmail('');
      setNotes('');
      setOob([newOobDraft()]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, profile?.id]);

  const { data: customerList } = useQuery({
    queryKey: ['customers', { per_page: 500 }],
    queryFn: () => customers.list({ per_page: 500 }),
    enabled: open && !editing,
  });
  const { data: platformList } = useQuery({
    queryKey: ['customer-platforms', customerId],
    queryFn: () => customerPlatforms.forCustomer(customerId),
    enabled: open && customerId > 0,
  });
  const { data: testerList } = useQuery({
    queryKey: ['testers', { per_page: 500 }],
    queryFn: () => testers.list({ per_page: 500 }),
    enabled: open,
  });
  const { data: oobTypeList } = useQuery({
    queryKey: ['lookups', 'oob-types'],
    queryFn: () => lookups.oobTypes.list(),
    enabled: open,
  });

  const platform = useMemo(
    () => platformList?.data.find((p) => p.id === platformId),
    [platformList, platformId],
  );
  const mode = platform?.profile_mode ?? profile?.profile_mode;
  const oobTypes = oobTypeList?.data ?? [];
  const testerRows = (testerList?.data ?? []).filter(
    (t) => t.tester_status_name !== 'Inactive' || t.id === testerId,
  );

  const errs: Record<string, string> = {};
  if (!platformId) errs.platform = 'Select a platform';
  if (mode && mode !== 'shared' && !testerId) errs.tester = 'Select the tester';
  if (mode === 'per_tester_multi' && !label.trim()) errs.label = 'A label is required for this platform';
  if (!phoneId) errs.phone = 'Select a phone number';
  if (!isValidEmail(email.trim())) errs.email = 'Enter a valid email address';
  const oobErrors: OobErrors[] = oob.map((d) => validateOobDraft(d, oobTypes));
  const hasErrors = Object.keys(errs).length > 0 || oobErrors.some((e) => Object.keys(e).length > 0);
  const shown = showErrors ? errs : {};

  const save = useMutation({
    mutationFn: () => {
      const body = {
        customer_platform_id: platformId,
        tester_id: mode === 'shared' ? null : testerId || null,
        label: label.trim() || null,
        ...Object.fromEntries(ADDRESS_FIELDS.map((f) => [f.key, address[f.key].trim() || null])),
        phone_id: phoneId,
        email: email.trim(),
        notes: notes.trim() || null,
        oob_entries: oob.map(oobDraftToInput),
      };
      return profile ? customerProfiles.update(profile.id, body) : customerProfiles.create(body);
    },
    onSuccess: (p) => {
      for (const key of ['customer-profiles', 'customer-profile', 'customer-platforms', 'accounts', 'account']) {
        void queryClient.invalidateQueries({ queryKey: [key] });
      }
      onSaved(p);
      onClose();
    },
  });

  const customerOptions = (customerList?.data ?? []).filter((c) => c.is_customer !== false);
  const platformOptions = (platformList?.data ?? []).filter((p) => p.active || p.id === platformId);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={profile ? `Edit profile ${profile.display}` : 'New online profile'}
      className="max-w-3xl max-h-[90vh] overflow-y-auto"
      footer={
        <>
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="button"
            disabled={save.isPending}
            onClick={() => {
              setShowErrors(true);
              if (!hasErrors) save.mutate();
            }}
          >
            {save.isPending ? 'Saving…' : 'Save profile'}
          </Button>
        </>
      }
    >
      <div
        className="space-y-4"
        // The dialog can open inside the account <form>; Enter must not submit it.
        onKeyDown={(e) => {
          if (e.key === 'Enter' && (e.target as HTMLElement).tagName === 'INPUT') e.preventDefault();
        }}
      >
        <section className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="FRT Customer *">
            {editing ? (
              <div className="py-2">
                {profile!.frt_customer_code} ({profile!.frt_customer_name})
              </div>
            ) : (
              <select
                value={customerId}
                onChange={(e) => {
                  setCustomerId(Number(e.target.value));
                  setPlatformId(0);
                }}
                className={selectClass}
              >
                <option value={0}>— select —</option>
                {customerOptions.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.code} ({c.name})
                  </option>
                ))}
              </select>
            )}
          </Field>
          <Field label="Platform *" error={shown.platform}>
            {editing ? (
              <div className="py-2">
                {platformLabel({ name: profile!.platform_name, type: profile!.platform_type_name })} ·{' '}
                {modeLabel(profile!.profile_mode)}
              </div>
            ) : (
              <select
                value={platformId}
                onChange={(e) => setPlatformId(Number(e.target.value))}
                className={selectClass}
                disabled={!customerId}
              >
                <option value={0}>{customerId ? '— select —' : 'Pick a customer first'}</option>
                {platformOptions.map((p) => (
                  <option key={p.id} value={p.id}>
                    {platformLabel({ name: p.name, type: p.platform_type_name })} · {modeLabel(p.profile_mode)}
                  </option>
                ))}
              </select>
            )}
            {!editing && customerId > 0 && platformList && platformOptions.length === 0 && (
              <span className="block text-xs text-amber-700 mt-0.5">
                This customer has no platforms yet — add one on the customer’s page.
              </span>
            )}
          </Field>
          {mode !== 'shared' && (
            <Field label="Tester *" error={shown.tester}>
              <select
                value={testerId}
                onChange={(e) => setTesterId(Number(e.target.value))}
                className={selectClass}
              >
                <option value={0}>— select —</option>
                {testerRows.map((t) => (
                  <option key={t.id} value={t.id}>
                    {testerLabel(t)}
                  </option>
                ))}
              </select>
            </Field>
          )}
          <Field
            label={mode === 'per_tester_multi' ? 'Label *' : 'Label'}
            error={shown.label}
          >
            <Input
              maxLength={PROFILE_TEXT_MAX}
              value={label}
              placeholder={mode === 'per_tester_multi' ? 'e.g. Main, Trading' : 'Optional'}
              onChange={(e) => setLabel(e.target.value)}
            />
          </Field>
        </section>

        <section className="space-y-2">
          <h3 className="text-sm font-semibold text-slate-800">Contact</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Phone number *" error={shown.phone}>
              <PhonePicker value={phoneId} onChange={setPhoneId} />
            </Field>
            <Field label="Email *" error={shown.email}>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
            </Field>
            {ADDRESS_FIELDS.map((f) => (
              <Field key={f.key} label={f.label} className={f.wide ? 'sm:col-span-2' : ''}>
                <Input
                  maxLength={f.key === 'postal_code' ? 20 : PROFILE_TEXT_MAX}
                  value={address[f.key]}
                  onChange={(e) => setAddress({ ...address, [f.key]: e.target.value })}
                />
              </Field>
            ))}
          </div>
        </section>

        <section className="space-y-2">
          <h3 className="text-sm font-semibold text-slate-800">Account Access (OTP channels)</h3>
          <p className="text-xs text-slate-500">At least one is required.</p>
          <OobEditor
            entries={oob}
            onChange={setOob}
            errors={showErrors ? oobErrors : []}
            types={oobTypes}
          />
        </section>

        <Field label="Notes">
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            className="block w-full rounded border border-slate-300 px-2 py-1.5 text-sm"
          />
        </Field>
        {save.error && <p className="text-sm text-red-600">{(save.error as Error).message}</p>}
      </div>
    </Dialog>
  );
}
